import { Link, useParams } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminOrgAction, useAdminOrgDetail } from '@/lib/hooks/useAdmin';
import { useMe } from '@/lib/hooks/useBootstrap';

export function AdminOrgDetailPage() {
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const { data: me } = useMe(true);
  const { data, isLoading } = useAdminOrgDetail(orgId);
  const action = useAdminOrgAction();

  if (me && !me.user.is_admin) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center text-sm text-slate-500">
        Restricted.
      </div>
    );
  }

  if (isLoading || !data) {
    return <div className="mx-auto max-w-6xl px-8 py-10 text-sm text-slate-500">Loading…</div>;
  }

  const o = data.org;
  const run = (act: 'suspend' | 'unsuspend' | 'founding-customer' | 'audit') => {
    if (act === 'suspend' && !confirm(`Suspend ${o.slug}?`)) return;
    action.mutate({ orgId, action: act });
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <p className="text-sm text-slate-500">
        <Link to="/_admin/orgs" className="hover:underline">_admin · orgs</Link> · <span className="font-mono">{o.slug}</span>
      </p>
      <h1 className="mt-1 text-3xl font-semibold tracking-tight">{o.name}</h1>

      <div className="mt-4 flex flex-wrap gap-2">
        {o.suspended ? (
          <Button variant="outline" onClick={() => run('unsuspend')}>Unsuspend</Button>
        ) : (
          <Button variant="destructive" onClick={() => run('suspend')}>Suspend</Button>
        )}
        <Button variant="outline" onClick={() => run('founding-customer')}>
          {o.founding_customer ? 'Remove founding flag' : 'Mark founding'}
        </Button>
        <Button variant="outline" onClick={() => run('audit')}>Force re-audit</Button>
      </div>

      {action.data?.message && (
        <p className="mt-3 text-xs text-[var(--color-success)]">{action.data.message}</p>
      )}

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Org</CardTitle>
            <CardDescription>Tier {o.tier} · {o.members} member{o.members === 1 ? '' : 's'} · Created {new Date(o.created_at).toLocaleDateString()}</CardDescription>
          </CardHeader>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <dt className="text-slate-500">Slug</dt><dd className="font-mono">{o.slug}</dd>
            <dt className="text-slate-500">Domain</dt><dd>{o.domain ?? '—'}</dd>
            <dt className="text-slate-500">Status</dt><dd>{o.status}</dd>
            <dt className="text-slate-500">Founding</dt><dd>{o.founding_customer ? 'yes' : 'no'}</dd>
          </dl>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Usage this period</CardTitle>
            <CardDescription>{data.usage_this_period.period}</CardDescription>
          </CardHeader>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <dt className="text-slate-500">Monitored PRs</dt><dd>{data.usage_this_period.monitored_prs}</dd>
            <dt className="text-slate-500">Audits</dt><dd>{data.usage_this_period.audits_run}</dd>
            <dt className="text-slate-500">API calls</dt><dd>{data.usage_this_period.api_calls}</dd>
          </dl>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>{data.members.length} total</CardDescription>
        </CardHeader>
        <table className="w-full text-xs">
          <thead className="text-left uppercase tracking-wide text-slate-500">
            <tr>
              <th className="pb-2 pr-4">Email</th><th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Role</th><th className="pb-2">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {data.members.map((m) => (
              <tr key={m.user_id}>
                <td className="py-2 pr-4 font-mono">{m.email}</td>
                <td className="py-2 pr-4">{m.full_name ?? '—'}</td>
                <td className="py-2 pr-4">{m.role}</td>
                <td className="py-2 text-slate-500">{new Date(m.joined_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="mt-4">
        <CardHeader><CardTitle>Recent audits</CardTitle></CardHeader>
        {data.recent_audits.length === 0 ? <p className="text-xs text-slate-500">None.</p> : (
          <ul className="space-y-1 text-xs">
            {data.recent_audits.map((a) => (
              <li key={a.id}>
                <span className="font-mono text-slate-500">{a.id.slice(0, 8)}</span>
                {' · '}{a.status}{' · '}{a.trigger}
                {a.overall_score != null && <> · score {a.overall_score}</>}
                {' · '}<span className="text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-4">
        <CardHeader><CardTitle>Recent PR checks</CardTitle></CardHeader>
        {data.recent_pr_checks.length === 0 ? <p className="text-xs text-slate-500">None.</p> : (
          <ul className="space-y-1 text-xs">
            {data.recent_pr_checks.map((p) => (
              <li key={p.id}>
                <span className="font-mono">{p.repo_full_name} #{p.pr_number}</span>
                {' · '}{p.status}{p.skipped_reason ? ` (${p.skipped_reason})` : ''}
                {' · '}<span className="text-slate-500">{new Date(p.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
